import compose from 'lodash.flowright';
import { Check, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { matchPath, withRouter } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from 'shadcn/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from 'shadcn/command';
import { cn } from 'shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/popover';
import type { PrivilegedUser } from 'utils/elevatedRights';
import { withUser } from 'wrappers';
import { SCENARIO_RANK, type Scenario, ScenarioBadge } from './ScenarioBadge';

type OrgRef = {
  rowId: string;
  name: string;
  avatar?: string | null;
  organizationType?: string | null;
};

type OrgNode = OrgRef & {
  id?: string;
  primaryOrganization?: OrgRef | null;
};

type OrgsConnection = { nodes: readonly OrgNode[] };

type Props = {
  location?: { pathname?: string } | null;
  user: PrivilegedUser & { organizations?: OrgsConnection | null };
  selectedOrganization: OrgNode | null;
  superUserOrgs: OrgsConnection | null;
  switchOrganization: (org: OrgNode) => void;
};

const byName = (a: OrgRef, b: OrgRef) =>
  (a.name || '').localeCompare(b.name || '');

const FALLBACK_RANK = 99;

const isScenario = (value: string | null | undefined): value is Scenario =>
  value === 'prod' || value === 'dev' || value === 'demo' || value === 'test';

const byScenario = (a: OrgRef, b: OrgRef) => {
  const aRank = isScenario(a.organizationType)
    ? SCENARIO_RANK[a.organizationType]
    : FALLBACK_RANK;
  const bRank = isScenario(b.organizationType)
    ? SCENARIO_RANK[b.organizationType]
    : FALLBACK_RANK;
  return aRank - bRank;
};

const initialsOf = (name?: string | null) =>
  (name || '')
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

type OrgAvatarProps = {
  org: OrgNode | OrgRef | null | undefined;
  size?: string;
};

const OrgAvatar = ({ org, size = 'h-7 w-7' }: OrgAvatarProps) => (
  <Avatar className={cn(size, 'shrink-0 ring-1 ring-black/10')}>
    <AvatarImage alt={org?.name} src={org?.avatar ?? undefined} />
    <AvatarFallback className="text-xs">{initialsOf(org?.name)}</AvatarFallback>
  </Avatar>
);

type ScenarioRowProps = {
  org: OrgNode;
  selected: boolean;
  onClick: () => void;
};

const ScenarioRow = ({ org, selected, onClick }: ScenarioRowProps) => (
  <button
    className={cn(
      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left transition-[background-color] hover:bg-neutral-100',
      selected && 'bg-neutral-100 font-medium'
    )}
    data-selected={selected}
    data-testid="scenario-switch-item"
    onClick={onClick}
    type="button"
  >
    <ScenarioBadge type={org.organizationType} />
  </button>
);

export const ChangeOrganization = ({
  location,
  user,
  selectedOrganization,
  superUserOrgs,
  switchOrganization
}: Props) => {
  const [open, setOpen] = useState(false);

  const orgs: OrgNode[] = [
    ...(superUserOrgs?.nodes ?? user.organizations?.nodes ?? [])
  ];

  // Resolve the current org from the URL (`:orgId`) — pages key off it too —
  // falling back to the context's selected org.
  const orgIdFromUrl = matchPath<{ orgId?: string }>(location?.pathname ?? '', {
    path: '/organization/:orgId'
  })?.params?.orgId;
  const current: OrgNode | null =
    orgs.find(org => String(org.rowId) === String(orgIdFromUrl)) ??
    selectedOrganization;

  // An org's primary is only meaningful when that primary is itself accessible
  // to the user. A satellite whose primary is missing from the list (e.g. a
  // test org whose prod parent the user is not a member of) must be treated as
  // a standalone main, never grouped under an org it cannot reach.
  const isAccessible = (rowId: string) => orgs.some(org => org.rowId === rowId);
  const accessiblePrimaryOf = (org: OrgNode | null | undefined) =>
    org?.primaryOrganization && isAccessible(org.primaryOrganization.rowId)
      ? org.primaryOrganization
      : null;

  // "main" orgs are those without an accessible primary; satellites link to one.
  const mains = orgs.filter(org => !accessiblePrimaryOf(org));
  const satellitesOf = (mainId: string) =>
    orgs.filter(org => accessiblePrimaryOf(org)?.rowId === mainId);

  // The main of the current org's group (the satellite's accessible primary, or
  // itself when there is none).
  const currentPrimary = accessiblePrimaryOf(current);
  const currentMain: OrgNode | null = currentPrimary
    ? (orgs.find(org => org.rowId === currentPrimary.rowId) ?? current)
    : current;
  const groupOrgs: OrgNode[] = currentMain
    ? [currentMain, ...satellitesOf(currentMain.rowId)]
    : [];

  const canSwitchMains = mains.length > 1;
  const hasGroupSwitch = groupOrgs.length > 1;

  // When the user only has access to a satellite whose primary org is not
  // accessible, still surface the primary (prod) org's name — it is carried as
  // metadata on the satellite — so the header reads e.g. "AXA Global Re"
  // instead of "AXA Global Re [TEST]".
  const inaccessiblePrimaryName =
    !currentPrimary && current?.primaryOrganization
      ? current.primaryOrganization.name
      : null;
  const displayName = inaccessiblePrimaryName ?? currentMain?.name;

  // Show a read-only type badge for that standalone satellite (there is no
  // group to switch within, so the interactive env dropdown is not rendered).
  const showStandaloneTypeBadge =
    !hasGroupSwitch && Boolean(inaccessiblePrimaryName);

  const mainIdentity = (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <OrgAvatar org={currentMain} />
      <span className="truncate font-medium text-base text-neutral-800">
        {displayName}
      </span>
      {showStandaloneTypeBadge && (
        <ScenarioBadge type={current?.organizationType} />
      )}
    </span>
  );

  return (
    // Horizontal layout: the main control + (optional) compact scenario badge dropdown.
    <div className="flex w-full min-w-0 items-center gap-2">
      {/* Primary control: switch between main organizations. */}
      {canSwitchMains ? (
        <Popover onOpenChange={setOpen} open={open}>
          <PopoverTrigger asChild>
            <button
              aria-label="Switch organization"
              className="flex min-w-0 flex-1 items-center justify-between gap-1 rounded-md px-1 py-0.5 text-left transition-[background-color] hover:bg-neutral-200/50"
              data-testid="org-switcher-trigger"
              type="button"
            >
              {mainIdentity}
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-neutral-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-(--radix-popover-trigger-width) min-w-64 p-0"
          >
            <Command>
              <CommandInput placeholder="Search organization…" />
              <CommandList>
                <CommandEmpty>No organization found.</CommandEmpty>
                <CommandGroup>
                  {[...mains].sort(byName).map(main => {
                    const selected = main.rowId === currentMain?.rowId;
                    return (
                      <CommandItem
                        className="gap-2 text-neutral-700"
                        data-selected={selected}
                        data-testid="org-switch-item"
                        key={main.rowId}
                        keywords={[main.name]}
                        onSelect={() => {
                          switchOrganization(main);
                          setOpen(false);
                        }}
                        value={main.rowId}
                      >
                        <OrgAvatar org={main} size="h-5 w-5" />
                        <span className="truncate">{main.name}</span>
                        {selected && (
                          <Check className="ml-auto h-4 w-4 shrink-0 text-neutral-500" />
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : (
        <div
          className="flex min-w-0 flex-1 items-center px-1 py-0.5"
          data-testid="org-identity"
        >
          {mainIdentity}
        </div>
      )}

      {/* Compact scenario badge dropdown: switch between the main org and its satellites. */}
      {hasGroupSwitch && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              aria-label="Switch environment"
              className="flex shrink-0 items-center gap-1 rounded-md px-1 py-0.5 transition-[background-color] hover:bg-neutral-200/50"
              data-testid="org-env-trigger"
              type="button"
            >
              <ScenarioBadge type={current?.organizationType} />
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-32 p-1">
            {[...groupOrgs].sort(byScenario).map(org => (
              <ScenarioRow
                key={org.rowId}
                onClick={() => switchOrganization(org)}
                org={org}
                selected={org.rowId === current?.rowId}
              />
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default compose(withUser, withRouter)(ChangeOrganization);
